// Minimal allocation-free vector/quaternion helpers for the simulation (no three.js imports).

export interface V3 {
  x: number;
  y: number;
  z: number;
}

export interface Q4 {
  x: number;
  y: number;
  z: number;
  w: number;
}

export const v3 = (x = 0, y = 0, z = 0): V3 => ({ x, y, z });
export const q4 = (x = 0, y = 0, z = 0, w = 1): Q4 => ({ x, y, z, w });

export function set(o: V3, x: number, y: number, z: number): V3 {
  o.x = x;
  o.y = y;
  o.z = z;
  return o;
}
export function copy(o: V3, a: V3): V3 {
  o.x = a.x;
  o.y = a.y;
  o.z = a.z;
  return o;
}
export function add(o: V3, a: V3, b: V3): V3 {
  o.x = a.x + b.x;
  o.y = a.y + b.y;
  o.z = a.z + b.z;
  return o;
}
export function sub(o: V3, a: V3, b: V3): V3 {
  o.x = a.x - b.x;
  o.y = a.y - b.y;
  o.z = a.z - b.z;
  return o;
}
export function scale(o: V3, a: V3, s: number): V3 {
  o.x = a.x * s;
  o.y = a.y * s;
  o.z = a.z * s;
  return o;
}
export function addScaled(o: V3, a: V3, b: V3, s: number): V3 {
  o.x = a.x + b.x * s;
  o.y = a.y + b.y * s;
  o.z = a.z + b.z * s;
  return o;
}
export const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export function cross(o: V3, a: V3, b: V3): V3 {
  const x = a.y * b.z - a.z * b.y;
  const y = a.z * b.x - a.x * b.z;
  const z = a.x * b.y - a.y * b.x;
  o.x = x;
  o.y = y;
  o.z = z;
  return o;
}
export const len = (a: V3): number => Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
export function normalize(o: V3, a: V3): V3 {
  const l = len(a);
  if (l > 1e-12) return scale(o, a, 1 / l);
  return set(o, 0, 0, 0);
}

/** Rotate vector a by unit quaternion q into o. */
export function rotate(o: V3, q: Q4, a: V3): V3 {
  const { x, y, z } = a;
  const qx = q.x,
    qy = q.y,
    qz = q.z,
    qw = q.w;
  // t = 2 * cross(q.xyz, a)
  const tx = 2 * (qy * z - qz * y);
  const ty = 2 * (qz * x - qx * z);
  const tz = 2 * (qx * y - qy * x);
  o.x = x + qw * tx + (qy * tz - qz * ty);
  o.y = y + qw * ty + (qz * tx - qx * tz);
  o.z = z + qw * tz + (qx * ty - qy * tx);
  return o;
}

const _conj: Q4 = { x: 0, y: 0, z: 0, w: 1 };

/** Rotate by the inverse (conjugate) of unit quaternion q. */
export function rotateInv(o: V3, q: Q4, a: V3): V3 {
  _conj.x = -q.x;
  _conj.y = -q.y;
  _conj.z = -q.z;
  _conj.w = q.w;
  return rotate(o, _conj, a);
}

export function quatFromAxisAngle(o: Q4, ax: number, ay: number, az: number, angle: number): Q4 {
  const h = angle * 0.5;
  const s = Math.sin(h);
  o.x = ax * s;
  o.y = ay * s;
  o.z = az * s;
  o.w = Math.cos(h);
  return o;
}

export function quatMul(o: Q4, a: Q4, b: Q4): Q4 {
  const x = a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y;
  const y = a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x;
  const z = a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w;
  const w = a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z;
  o.x = x;
  o.y = y;
  o.z = z;
  o.w = w;
  return o;
}

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};
export const sign = (v: number): number => (v > 0 ? 1 : v < 0 ? -1 : 0);

/** Move `cur` toward `target` by at most `maxDelta`. */
export function approach(cur: number, target: number, maxDelta: number): number {
  if (cur < target) return Math.min(cur + maxDelta, target);
  return Math.max(cur - maxDelta, target);
}

/** Piecewise-linear interpolation over sorted [x, y] pairs. */
export function interp1(table: ReadonlyArray<readonly [number, number]>, x: number): number {
  const n = table.length;
  if (x <= table[0][0]) return table[0][1];
  if (x >= table[n - 1][0]) return table[n - 1][1];
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (table[mid][0] <= x) lo = mid;
    else hi = mid;
  }
  const [x0, y0] = table[lo];
  const [x1, y1] = table[hi];
  return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
}

/** Deterministic PRNG (mulberry32). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const KMH = 3.6;
export const RPM_TO_RADS = (2 * Math.PI) / 60;
export const RADS_TO_RPM = 60 / (2 * Math.PI);
export const DEG = Math.PI / 180;
export const G = 9.81;
